import NiceDropdown from 'components/NiceDropdown';
import React, { useMemo, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Box, Button } from 'rebass';

/**
 * Formats a sender profile into a display title
 *
 * @param {Object} senderProfile - The sender profile object
 * @param {string} senderProfile.name - Display name of the sender
 * @param {string} senderProfile.email - Email address of the sender
 * @returns {string} Formatted title in "Name <email>" format
 */
function getSenderProfileTitle(senderProfile) {
    return `${senderProfile.name} <${senderProfile.email}>`;
}

/**
 * Gets the display text for the currently selected sender profile
 *
 * @param {Array<Object>} senderProfiles - Array of available sender profiles
 * @param {string|number} selectedSenderProfileId - ID of selected profile
 * @returns {string} The formatted title of selected sender profile or fallback text
 */
function displaySelectedSenderProfile(senderProfiles, selectedSenderProfileId) {
    if (selectedSenderProfileId) {
        const senderProfile = senderProfiles.find(senderProfile => senderProfile.id === selectedSenderProfileId);

        if (senderProfile) {
            return getSenderProfileTitle(senderProfile);
        }
    }
    return 'No profile selected';
}

/**
 * A dropdown component for selecting sender profiles for email campaigns
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.senderProfiles - Array of sender profile objects
 * @param {string} props.senderProfiles[].id - Unique identifier for the sender profile
 * @param {string} props.senderProfiles[].name - Display name of the sender
 * @param {string} props.senderProfiles[].email - Email address of the sender
 * @param {string|number} [props.defaultValue] - Default selected sender profile ID
 * @param {function} props.onChange - Callback function called when selection changes
 * @returns {JSX.Element} The sender profile select component
 */
function SenderProfileSelect({ senderProfiles, defaultValue, onChange }) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [value, setValue] = useState(defaultValue);

    const dropdownItems = useMemo(
        () =>
            senderProfiles.map(senderProfile => ({
                id: senderProfile.id,
                title: getSenderProfileTitle(senderProfile)
            })),
        [senderProfiles]
    );

    return (
        <Box sx={{ position: 'relative' }}>
            <Button
                type="button"
                variant="secondary-gray"
                style={{ maxWidth: '320px' }}
                onClick={() => setShowDropdown(true)}
            >
                {displaySelectedSenderProfile(senderProfiles, value)}
                <FiChevronDown
                    style={{
                        marginRight: 0
                    }}
                />
            </Button>
            {showDropdown && (
                <NiceDropdown
                    onClose={() => setShowDropdown(false)}
                    items={dropdownItems}
                    onChange={id => {
                        setValue(id);
                        onChange(id);
                    }}
                />
            )}
        </Box>
    );
}

export default SenderProfileSelect;
